import { useCallback, useState } from 'react'

import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { useLazyQuery } from '@apollo/client/react'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

export const SEARCH_INSTAGRAM_AUDIO_CATALOG_QUERY = graphql(`
  query SearchInstagramAudioCatalog($input: SearchInstagramAudioCatalogInput!) {
    searchInstagramAudioCatalog(input: $input) {
      __typename
      ... on InstagramAudioCatalogSuccess {
        audio {
          id
          type
          title
          displayArtist
          creatorUsername
          duration
          previewUrl
          coverArtworkUrl
        }
        failedAudioTypes
      }
      ... on ChannelRefreshRequired {
        message
        channelIds
      }
    }
  }
`)

export const INSTAGRAM_AUDIO_CATALOG_QUERY = graphql(`
  query InstagramAudioCatalog($input: InstagramAudioCatalogInput!) {
    instagramAudioCatalog(input: $input) {
      __typename
      ... on InstagramAudioCatalogSuccess {
        audio {
          id
          type
          title
          displayArtist
          creatorUsername
          duration
          previewUrl
          coverArtworkUrl
        }
        failedAudioTypes
      }
      ... on ChannelRefreshRequired {
        message
        channelIds
      }
    }
  }
`)

export const INSTAGRAM_AUDIO_PREVIEW_QUERY = graphql(`
  query InstagramAudioPreview($input: InstagramAudioPreviewInput!) {
    instagramAudioPreview(input: $input) {
      __typename
      ... on InstagramAudioPreviewSuccess {
        dataUrl
      }
      ... on ChannelRefreshRequired {
        message
        channelIds
      }
    }
  }
`)

type InstagramAudioCatalogSuccess = Extract<
  NonNullable<
    ResultOf<typeof INSTAGRAM_AUDIO_CATALOG_QUERY>['instagramAudioCatalog']
  >,
  { __typename: 'InstagramAudioCatalogSuccess' }
>

export type InstagramAudioResult = InstagramAudioCatalogSuccess['audio'][number]

export type InstagramAudioType = InstagramAudioResult['type']

const EMPTY_AUDIO: InstagramAudioResult[] = []
const EMPTY_AUDIO_TYPES: InstagramAudioType[] = []

function getApolloErrorMessage(error: unknown): string | null {
  if (CombinedGraphQLErrors.is(error)) {
    return error.errors[0]?.message ?? null
  }

  if (error instanceof Error && error.message.length > 0) {
    return error.message
  }

  return null
}

export function useSearchInstagramAudio(): {
  searchAudio: (args: { channelId: string; query?: string }) => Promise<void>
  fetchPreviewDataUrl: (args: {
    channelId: string
    audioId: string
    previewUrl?: string | null
  }) => Promise<string>
  audio: InstagramAudioResult[]
  failedAudioTypes: InstagramAudioType[]
  loading: boolean
  errorMessage: string | null
  needsChannelRefresh: boolean
} {
  const [requestMode, setRequestMode] = useState<'search' | 'trending'>(
    'trending',
  )
  const [previewRefreshMessage, setPreviewRefreshMessage] = useState<
    string | null
  >(null)
  const [searchInstagramAudioCatalog, searchResult] = useLazyQuery(
    SEARCH_INSTAGRAM_AUDIO_CATALOG_QUERY,
    {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
  )
  const [instagramAudioCatalog, trendingResult] = useLazyQuery(
    INSTAGRAM_AUDIO_CATALOG_QUERY,
    {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
  )

  const [fetchInstagramAudioPreview] = useLazyQuery(
    INSTAGRAM_AUDIO_PREVIEW_QUERY,
    {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
  )

  const searchAudio = useCallback(
    async (args: { channelId: string; query?: string }): Promise<void> => {
      setPreviewRefreshMessage(null)
      const query = args.query?.trim()
      if (query) {
        setRequestMode('search')
        await searchInstagramAudioCatalog({
          variables: { input: { channelId: args.channelId, query } },
        })
        return
      }

      setRequestMode('trending')
      await instagramAudioCatalog({
        variables: { input: { channelId: args.channelId } },
      })
    },
    [searchInstagramAudioCatalog, instagramAudioCatalog],
  )

  const fetchPreviewDataUrl = useCallback(
    async (args: {
      channelId: string
      audioId: string
      previewUrl?: string | null
    }): Promise<string> => {
      setPreviewRefreshMessage(null)
      const result = await fetchInstagramAudioPreview({
        variables: {
          input: {
            channelId: args.channelId,
            audioId: args.audioId,
            ...(args.previewUrl ? { previewUrl: args.previewUrl } : {}),
          },
        },
      })

      const payload = result.data?.instagramAudioPreview
      if (payload?.__typename === 'InstagramAudioPreviewSuccess') {
        return payload.dataUrl
      }
      if (payload?.__typename === 'ChannelRefreshRequired') {
        setPreviewRefreshMessage(payload.message)
        throw new Error(payload.message)
      }
      if (result.error) {
        const graphQLMessage = getApolloErrorMessage(result.error)
        throw new Error(graphQLMessage ?? 'Failed to load audio preview')
      }
      throw new Error('Failed to load audio preview')
    },
    [fetchInstagramAudioPreview],
  )

  let audio: InstagramAudioResult[] = EMPTY_AUDIO
  let failedAudioTypes: InstagramAudioType[] = EMPTY_AUDIO_TYPES
  let errorMessage: string | null = null
  let needsChannelRefresh = false
  const response =
    requestMode === 'search'
      ? searchResult.data?.searchInstagramAudioCatalog
      : trendingResult.data?.instagramAudioCatalog
  const loading =
    requestMode === 'search' ? searchResult.loading : trendingResult.loading
  const error =
    requestMode === 'search' ? searchResult.error : trendingResult.error

  if (response?.__typename === 'InstagramAudioCatalogSuccess') {
    audio = response.audio ?? EMPTY_AUDIO
    failedAudioTypes = response.failedAudioTypes ?? EMPTY_AUDIO_TYPES
  } else if (response?.__typename === 'ChannelRefreshRequired') {
    needsChannelRefresh = true
    errorMessage = response.message
  } else if (error) {
    errorMessage = 'Failed to search Instagram audio'
  }

  if (previewRefreshMessage !== null) {
    needsChannelRefresh = true
    errorMessage = previewRefreshMessage
  }

  return {
    searchAudio,
    fetchPreviewDataUrl,
    audio,
    failedAudioTypes,
    loading,
    errorMessage,
    needsChannelRefresh,
  }
}
